import { CSSProperties, FC, useEffect, useRef, useState } from 'react'

// YARD HAUL — TOS easter egg, the snake-style game.
// You drive a terminal tractor with a flatbed trailer. Pick up containers from
// the yard; deliver them to cranes that match their destination. The trailer
// holds 4 containers — load up, plan your route, hit the right cranes.
//
// Controls:
//   ← ↑ → ↓     turn the tractor
//   space       pause
//   R           restart
//   M           mute
//   ESC         close
//
// CUSTOMIZE: search for `// CUSTOMIZE:` for tunable bits.

// ---- grid ----
const COLS = 28
const ROWS = 18
const CELL = 26

// ---- gameplay ----
const TICK_FRAMES_BASE = 11
const TICK_FRAMES_FLOOR = 4
const CRANE_PICKUPS_MIN = 3
const CRANE_PICKUPS_MAX = 5
const CRANE_TICK_DELAY_MIN = 4
const SCORE_PER_DELIVERY = 100
const COMBO_BONUS_PER = 50

// CUSTOMIZE: company name for the game-over message.
const COMPANY_NAME = 'TERMINAL OS'

interface Destination {
  code: string
  color: string
}

const DESTINATIONS: Destination[] = [
  { code: 'LAX', color: '#3a8fd6' },
  { code: 'NYC', color: '#d97a2c' },
  { code: 'ROT', color: '#3aa15a' },
  { code: 'SHA', color: '#d94a8f' },
  { code: 'SIN', color: '#e6c14a' },
  { code: 'HAM', color: '#c43838' },
]

function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min
}
function randInt(min: number, max: number): number {
  return Math.floor(rand(min, max + 1))
}
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

function bicCode(prefix: string): string {
  let digits = ''
  for (let i = 0; i < 7; i++) digits += Math.floor(Math.random() * 10)
  return `${prefix}U ${digits}`
}

// ---- AUDIO ----
type WindowWithWebkitAudio = Window & {
  webkitAudioContext?: typeof AudioContext
}

class GameAudio {
  ctx: AudioContext | null = null
  masterGain: GainNode | null = null
  muted = false

  init() {
    if (this.ctx) return
    try {
      const Ctor = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext
      if (!Ctor) return
      this.ctx = new Ctor()
      this.masterGain = this.ctx.createGain()
      this.masterGain.gain.value = 0.22
      this.masterGain.connect(this.ctx.destination)
    } catch {
      // audio unavailable — fail silently
    }
  }
  private env(node: GainNode, attack: number, decay: number, peak = 1) {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    node.gain.setValueAtTime(0, t)
    node.gain.linearRampToValueAtTime(peak, t + attack)
    node.gain.exponentialRampToValueAtTime(0.001, t + attack + decay)
  }
  pickup() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.setValueAtTime(440, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(880, this.ctx.currentTime + 0.07)
    this.env(gain, 0.001, 0.08, 0.18)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.1)
  }
  deliver(combo: number) {
    if (!this.ctx || !this.masterGain || this.muted) return
    const baseFreq = 660 + combo * 80
    ;[baseFreq, baseFreq * 1.5].forEach((freq, i) => {
      if (!this.ctx || !this.masterGain) return
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      const t = this.ctx.currentTime + i * 0.06
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.2, t + 0.005)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2)
      osc.connect(gain).connect(this.masterGain)
      osc.start(t)
      osc.stop(t + 0.22)
    })
  }
  reject() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.value = 180
    this.env(gain, 0.005, 0.12, 0.12)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.13)
  }
  turn() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'triangle'
    osc.frequency.value = 220
    this.env(gain, 0.001, 0.03, 0.06)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.04)
  }
  crash() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const dur = 0.6
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 600
    const gain = this.ctx.createGain()
    this.env(gain, 0.005, dur, 0.6)
    src.connect(filter).connect(gain).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur)

    const osc = this.ctx.createOscillator()
    const gain2 = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(220, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(60, this.ctx.currentTime + 0.4)
    this.env(gain2, 0.005, 0.4, 0.3)
    osc.connect(gain2).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.45)
  }
  toggleMute() {
    this.muted = !this.muted
  }
}

// ---- world types ----
interface GridCell {
  col: number
  row: number
}

interface Direction {
  dx: number
  dy: number
}

interface YardContainer {
  col: number
  row: number
  dest: Destination
  code: string
  bob: number
}

interface Trailer {
  col: number
  row: number
  dest: Destination
  code: string
}

interface CraneEntity {
  col: number
  row: number
  dest: Destination
  pulse: number
  armSwing: number
}

interface Floater {
  x: number
  y: number
  text: string
  color: string
  life: number
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  color: string
  size: number
}

type GameStatus = 'ready' | 'playing' | 'paused' | 'gameover'

interface GameState {
  cab: GridCell
  trailers: Trailer[]
  dir: Direction
  nextDir: Direction
  container: YardContainer | null
  crane: CraneEntity | null
  pickupsSinceDelivery: number
  pickupsNeededForCrane: number
  craneTickCountdown: number
  score: number
  hi: number
  status: GameStatus
  tickFrames: number
  tickCounter: number
  time: number
  floaters: Floater[]
  particles: Particle[]
  shake: number
  speedLevel: number
}

interface HudState {
  score: number
  hi: number
  carrying: number
  status: GameStatus
  muted: boolean
}

// ---- world helpers ----
function isOccupied(
  col: number,
  row: number,
  truckSegments: GridCell[],
  container: YardContainer | null,
  crane: CraneEntity | null,
): boolean {
  if (truckSegments.some(s => s.col === col && s.row === row)) return true
  if (container && container.col === col && container.row === row) return true
  if (crane && crane.col === col && crane.row === row) return true
  return false
}

function spawnContainer(
  truckSegments: GridCell[],
  crane: CraneEntity | null,
): YardContainer | null {
  const free: GridCell[] = []
  for (let c = 0; c < COLS; c++) {
    for (let r = 0; r < ROWS; r++) {
      if (!isOccupied(c, r, truckSegments, null, crane)) free.push({ col: c, row: r })
    }
  }
  if (free.length === 0) return null
  const pos = pick(free)
  const dest = pick(DESTINATIONS)
  return {
    col: pos.col,
    row: pos.row,
    dest,
    code: bicCode(dest.code),
    bob: rand(0, Math.PI * 2),
  }
}

function spawnCrane(
  truckSegments: GridCell[],
  container: YardContainer | null,
  preferDest: Destination | null,
): CraneEntity | null {
  const minDistFromHead = 4
  const head = truckSegments[0]
  const free: GridCell[] = []
  for (let c = 1; c < COLS - 1; c++) {
    for (let r = 1; r < ROWS - 1; r++) {
      if (isOccupied(c, r, truckSegments, container, null)) continue
      const d = Math.abs(c - head.col) + Math.abs(r - head.row)
      if (d < minDistFromHead) continue
      let nearTruck = false
      for (const seg of truckSegments) {
        const dd = Math.abs(c - seg.col) + Math.abs(r - seg.row)
        if (dd <= 1) {
          nearTruck = true
          break
        }
      }
      if (nearTruck) continue
      free.push({ col: c, row: r })
    }
  }
  if (free.length === 0) return null
  const pos = pick(free)
  return {
    col: pos.col,
    row: pos.row,
    dest: preferDest || pick(DESTINATIONS),
    pulse: 0,
    armSwing: 0,
  }
}

interface YardHaulProps {
  onClose?: () => void
}

const YardHaul: FC<YardHaulProps> = ({ onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stateRef = useRef<GameState | null>(null)
  const audioRef = useRef<GameAudio>(new GameAudio())
  const startGameRef = useRef<(() => void) | null>(null)
  const [hud, setHud] = useState<HudState>({
    score: 0,
    hi: 0,
    carrying: 0,
    status: 'ready',
    muted: false,
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const audio = audioRef.current

    const dpr = window.devicePixelRatio || 1
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    let savedHi = 0
    try {
      savedHi = parseInt(localStorage.getItem('yardHaulHi') || '0', 10) || 0
    } catch {
      // localStorage unavailable
    }

    const initState = (): GameState => {
      const startCol = Math.floor(COLS / 2) + 1
      const startRow = Math.floor(ROWS / 2)
      const cab: GridCell = { col: startCol, row: startRow }
      const trailers: Trailer[] = []
      const container = spawnContainer([cab, ...trailers], null)
      return {
        cab,
        trailers,
        dir: { dx: 1, dy: 0 },
        nextDir: { dx: 1, dy: 0 },
        container,
        crane: null,
        pickupsSinceDelivery: 0,
        pickupsNeededForCrane: randInt(CRANE_PICKUPS_MIN, CRANE_PICKUPS_MAX),
        craneTickCountdown: 0,
        score: 0,
        hi: savedHi,
        status: 'ready',
        tickFrames: TICK_FRAMES_BASE,
        tickCounter: 0,
        time: 0,
        floaters: [],
        particles: [],
        shake: 0,
        speedLevel: 1,
      }
    }

    stateRef.current = initState()

    const startGame = () => {
      const s = initState()
      s.status = 'playing'
      stateRef.current = s
      setHud(p => ({ ...p, score: 0, carrying: 0, status: 'playing', hi: s.hi }))
    }
    startGameRef.current = startGame

    const tryQueueDir = (dx: number, dy: number) => {
      const s = stateRef.current
      if (!s || s.status !== 'playing') return
      if (s.dir.dx === -dx && s.dir.dy === -dy) return
      if (s.nextDir.dx === dx && s.nextDir.dy === dy) return
      s.nextDir = { dx, dy }
      audio.turn()
    }

    const onKeyDown = (e: KeyboardEvent) => {
      audio.init()
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key))
        e.preventDefault()
      if (e.key === 'Escape' && onClose) onClose()
      const s = stateRef.current
      if (!s) return
      if (s.status === 'ready' && (e.key === ' ' || e.key === 'Enter' || /^Arrow/.test(e.key))) {
        startGame()
        if (e.key === 'ArrowLeft') tryQueueDir(-1, 0)
        if (e.key === 'ArrowRight') tryQueueDir(1, 0)
        if (e.key === 'ArrowUp') tryQueueDir(0, -1)
        if (e.key === 'ArrowDown') tryQueueDir(0, 1)
        return
      }
      if (s.status === 'playing') {
        if (e.key === 'ArrowLeft') tryQueueDir(-1, 0)
        if (e.key === 'ArrowRight') tryQueueDir(1, 0)
        if (e.key === 'ArrowUp') tryQueueDir(0, -1)
        if (e.key === 'ArrowDown') tryQueueDir(0, 1)
        if (e.key === ' ') {
          s.status = 'paused'
          setHud(p => ({ ...p, status: 'paused' }))
        }
      } else if (s.status === 'paused' && e.key === ' ') {
        s.status = 'playing'
        setHud(p => ({ ...p, status: 'playing' }))
      }
      if (e.key === 'm' || e.key === 'M') {
        audio.toggleMute()
        setHud(p => ({ ...p, muted: audio.muted }))
      }
      if (e.key === 'r' || e.key === 'R') {
        const cur = s.status
        if (cur === 'gameover' || cur === 'ready') startGame()
        else if (cur === 'playing' || cur === 'paused') {
          if (window.confirm('Restart current shift? Your progress will be lost.')) startGame()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)

    const explode = (gridX: number, gridY: number, color: string, count = 14) => {
      const s = stateRef.current
      if (!s) return
      for (let i = 0; i < count; i++) {
        const a = rand(0, Math.PI * 2)
        const speed = rand(1, 4)
        s.particles.push({
          x: gridX,
          y: gridY,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed,
          life: rand(20, 40),
          color,
          size: rand(1.5, 3),
        })
      }
    }

    const tryDeliver = (): boolean => {
      const s = stateRef.current
      if (!s || !s.crane || s.trailers.length === 0) return false
      const d = Math.abs(s.cab.col - s.crane.col) + Math.abs(s.cab.row - s.crane.row)
      if (d !== 1) return false
      const idx = s.trailers.findIndex(t => s.crane && t.dest.code === s.crane.dest.code)
      if (idx === -1) return false
      const removed = s.trailers.splice(idx, 1)[0]

      const remaining = s.trailers.length
      const gain = SCORE_PER_DELIVERY + remaining * COMBO_BONUS_PER
      s.score += gain

      const cx = s.crane.col * CELL + CELL / 2
      const cy = s.crane.row * CELL + CELL / 2
      const label = remaining > 0 ? `+${gain} TEU · CHAIN ×${remaining + 1}` : `+${gain} TEU`
      s.floaters.push({ x: cx, y: cy - 8, text: label, color: '#ffd166', life: 80 })
      audio.deliver(remaining)
      explode(cx, cy, removed.dest.color, 16)

      s.crane = null
      s.pickupsSinceDelivery = 0
      s.pickupsNeededForCrane = randInt(CRANE_PICKUPS_MIN, CRANE_PICKUPS_MAX)
      s.craneTickCountdown = 0

      s.speedLevel = 1 + Math.floor(s.score / 1500)
      s.tickFrames = Math.max(TICK_FRAMES_FLOOR, TICK_FRAMES_BASE - (s.speedLevel - 1))
      return true
    }

    const gameOver = () => {
      const s = stateRef.current
      if (!s) return
      s.status = 'gameover'
      s.shake = 16
      audio.crash()
      const cab = s.cab
      explode(cab.col * CELL + CELL / 2, cab.row * CELL + CELL / 2, '#ffd166', 24)
      if (s.score > s.hi) {
        s.hi = s.score
        try {
          localStorage.setItem('yardHaulHi', String(s.hi))
        } catch {
          // localStorage unavailable
        }
      }
      setHud(p => ({ ...p, status: 'gameover', hi: s.hi }))
    }

    const tick = () => {
      const s = stateRef.current
      if (!s) return
      s.dir = s.nextDir

      const cab = s.cab
      const newCab: GridCell = {
        col: cab.col + s.dir.dx,
        row: cab.row + s.dir.dy,
      }

      if (newCab.col < 0 || newCab.col >= COLS || newCab.row < 0 || newCab.row >= ROWS) {
        gameOver()
        return
      }

      const willPickUp = !!(
        s.container &&
        s.container.col === newCab.col &&
        s.container.row === newCab.row
      )
      const collisionEnd = willPickUp ? s.trailers.length : s.trailers.length - 1
      for (let i = 0; i < collisionEnd; i++) {
        if (s.trailers[i].col === newCab.col && s.trailers[i].row === newCab.row) {
          gameOver()
          return
        }
      }

      if (s.crane && s.crane.col === newCab.col && s.crane.row === newCab.row) {
        audio.reject()
        s.nextDir = { ...s.dir }
        return
      }

      const oldCab: GridCell = { col: cab.col, row: cab.row }
      const oldTrailerPositions = s.trailers.map(t => ({ col: t.col, row: t.row }))
      s.cab = newCab
      for (let i = 0; i < s.trailers.length; i++) {
        const target = i === 0 ? oldCab : oldTrailerPositions[i - 1]
        s.trailers[i].col = target.col
        s.trailers[i].row = target.row
      }

      if (willPickUp && s.container) {
        const rearPos =
          s.trailers.length === 0 ? oldCab : oldTrailerPositions[oldTrailerPositions.length - 1]
        s.trailers.push({
          col: rearPos.col,
          row: rearPos.row,
          dest: s.container.dest,
          code: s.container.code,
        })
        s.pickupsSinceDelivery++
        audio.pickup()
        const cx = s.container.col * CELL + CELL / 2
        const cy = s.container.row * CELL + CELL / 2
        explode(cx, cy, s.container.dest.color, 8)
        s.container = spawnContainer([s.cab, ...s.trailers], s.crane)

        if (
          !s.crane &&
          s.pickupsSinceDelivery >= s.pickupsNeededForCrane &&
          s.craneTickCountdown === 0
        ) {
          s.craneTickCountdown = CRANE_TICK_DELAY_MIN
        }
      }

      tryDeliver()

      if (!s.crane && s.trailers.length > 0) {
        if (s.craneTickCountdown > 0) {
          s.craneTickCountdown--
          if (s.craneTickCountdown === 0) {
            const targetDest = pick(s.trailers).dest
            s.crane = spawnCrane([s.cab, ...s.trailers], s.container, targetDest)
            if (!s.crane) s.craneTickCountdown = 1
          }
        } else if (s.pickupsSinceDelivery >= s.pickupsNeededForCrane) {
          s.craneTickCountdown = CRANE_TICK_DELAY_MIN
        }
      }

      if (s.crane) s.crane.armSwing += 0.4
    }

    let animationId = 0

    const draw = () => {
      const s = stateRef.current
      if (!s) {
        animationId = requestAnimationFrame(draw)
        return
      }
      const w = canvas.width / dpr,
        h = canvas.height / dpr
      s.time++

      const playW = COLS * CELL
      const playH = ROWS * CELL
      const playX = Math.max(40, (w - playW) / 2)
      const playY = Math.max(80, (h - playH) / 2)

      let shakeX = 0,
        shakeY = 0
      if (s.shake > 0) {
        shakeX = rand(-s.shake, s.shake)
        shakeY = rand(-s.shake, s.shake)
        s.shake *= 0.85
        if (s.shake < 0.3) s.shake = 0
      }
      ctx.setTransform(dpr, 0, 0, dpr, shakeX * dpr, shakeY * dpr)

      const sky = ctx.createLinearGradient(0, 0, 0, h)
      sky.addColorStop(0, '#080e18')
      sky.addColorStop(1, '#0d1622')
      ctx.fillStyle = sky
      ctx.fillRect(0, 0, w, h)

      drawYardFrame(ctx, playX, playY, playW, playH)

      if (s.status === 'playing') {
        s.tickCounter++
        if (s.tickCounter >= s.tickFrames) {
          s.tickCounter = 0
          tick()
        }
        if (s.container) s.container.bob += 0.1
        if (s.crane) s.crane.pulse += 0.15

        s.floaters = s.floaters.filter(f => {
          f.y -= 0.5
          f.life--
          return f.life > 0
        })
        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vy += 0.2
          p.vx *= 0.96
          p.life--
          return p.life > 0
        })

        setHud(prev => {
          if (
            prev.score === s.score &&
            prev.carrying === s.trailers.length &&
            prev.status === s.status
          )
            return prev
          return { ...prev, score: s.score, carrying: s.trailers.length, status: s.status }
        })
      }

      if (s.container)
        drawYardContainer(
          ctx,
          playX + s.container.col * CELL,
          playY + s.container.row * CELL,
          s.container,
        )

      if (s.crane) drawCrane(ctx, playX + s.crane.col * CELL, playY + s.crane.row * CELL, s.crane)

      drawTruck(ctx, s.cab, s.trailers, s.dir, playX, playY, s.time)

      s.particles.forEach(p => {
        const alpha = Math.min(1, p.life / 25)
        ctx.fillStyle = p.color
        ctx.globalAlpha = alpha
        ctx.fillRect(p.x + playX - p.size / 2, p.y + playY - p.size / 2, p.size, p.size)
      })
      ctx.globalAlpha = 1

      s.floaters.forEach(f => {
        const alpha = Math.min(1, f.life / 30)
        ctx.fillStyle = f.color
        ctx.globalAlpha = alpha
        ctx.font = 'bold 12px ui-monospace, Menlo, monospace'
        ctx.textAlign = 'center'
        ctx.fillText(f.text, playX + f.x, playY + f.y)
      })
      ctx.globalAlpha = 1

      const vignette = ctx.createRadialGradient(w / 2, h / 2, h * 0.4, w / 2, h / 2, h)
      vignette.addColorStop(0, 'rgba(0,0,0,0)')
      vignette.addColorStop(1, 'rgba(0,0,0,0.5)')
      ctx.fillStyle = vignette
      ctx.fillRect(0, 0, w, h)

      animationId = requestAnimationFrame(draw)
    }
    animationId = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', resize)
    }
  }, [onClose])

  return (
    <div style={styles.root}>
      <div style={styles.hud}>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SCORE</span>
          <span style={styles.hudValue}>{String(hud.score).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BEST</span>
          <span style={styles.hudValue}>{String(hud.hi).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>TRAILERS</span>
          <span style={styles.hudValue}>{String(hud.carrying).padStart(2, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>AUDIO</span>
          <span style={styles.hudValue}>{hud.muted ? '○ MUTE' : '● ON'}</span>
        </div>
        <button
          style={styles.restart}
          onClick={() => {
            if (hud.status === 'playing' || hud.status === 'paused') {
              if (!window.confirm('Restart current shift? Your progress will be lost.')) return
            }
            audioRef.current.init()
            startGameRef.current?.()
          }}
          onMouseDown={e => e.preventDefault()}
          title='Restart shift (R)'
        >
          ↻ RESTART
        </button>
        {onClose && (
          <button style={styles.close} onClick={onClose}>
            ESC
          </button>
        )}
      </div>

      <canvas ref={canvasRef} style={styles.canvas} />

      {hud.status === 'ready' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>YARD HAUL</div>
          <div style={styles.overlaySubLarge}>
            collect containers · drop them at matching cranes
          </div>
          <div style={styles.controls}>
            ← ↑ → ↓ steer the tractor &nbsp;·&nbsp; SPACE pause &nbsp;·&nbsp; R restart
          </div>
          <div style={styles.controls}>
            <span style={{ color: '#5a6478' }}>
              every container picked up adds a trailer · road train keeps growing
              <br />
              cranes appear every few pickups · drive ADJACENT to one to drop a matching container
              <br />
              don&apos;t hit a wall · don&apos;t coil into your own trailers
            </span>
          </div>
          <div style={{ ...styles.controls, marginTop: 18 }}>press any arrow to start</div>
        </div>
      )}

      {hud.status === 'paused' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>YARD HOLD</div>
          <div style={styles.overlaySub}>press SPACE to resume</div>
        </div>
      )}

      {hud.status === 'gameover' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>HAUL ENDED</div>
          <div style={styles.overlaySub}>
            final score: {hud.score.toLocaleString()}{' '}
            {hud.score >= hud.hi && hud.score > 0 ? '· NEW BEST' : ''}
          </div>
          <pre style={styles.report}>
            {`SHIFT REPORT — ${COMPANY_NAME}
${hud.score.toLocaleString()} TEU moved
${hud.score >= hud.hi && hud.score > 0 ? 'operator commendation issued' : 'see you next shift'}`}
          </pre>
          <div style={styles.overlaySub}>press R or click RESTART to try again · ESC to close</div>
        </div>
      )}
    </div>
  )
}

export default YardHaul

// ============= DRAWING =============

function drawYardFrame(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  ctx.fillStyle = '#0d1622'
  ctx.fillRect(x, y, w, h)

  ctx.strokeStyle = 'rgba(60, 80, 110, 0.18)'
  ctx.lineWidth = 1
  for (let c = 1; c < COLS; c++) {
    ctx.beginPath()
    ctx.moveTo(x + c * CELL, y)
    ctx.lineTo(x + c * CELL, y + h)
    ctx.stroke()
  }
  for (let r = 1; r < ROWS; r++) {
    ctx.beginPath()
    ctx.moveTo(x, y + r * CELL)
    ctx.lineTo(x + w, y + r * CELL)
    ctx.stroke()
  }

  const cs = 16
  ctx.strokeStyle = '#ffd166'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(x, y + cs)
  ctx.lineTo(x, y)
  ctx.lineTo(x + cs, y)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(x + w - cs, y)
  ctx.lineTo(x + w, y)
  ctx.lineTo(x + w, y + cs)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(x, y + h - cs)
  ctx.lineTo(x, y + h)
  ctx.lineTo(x + cs, y + h)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(x + w - cs, y + h)
  ctx.lineTo(x + w, y + h)
  ctx.lineTo(x + w - cs, y + h)
  ctx.stroke()
}

function hexRgb(hex: string): string {
  const h = hex.replace('#', '')
  return `${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)}`
}

function drawYardContainer(
  ctx: CanvasRenderingContext2D,
  cellX: number,
  cellY: number,
  container: YardContainer,
) {
  const bob = Math.sin(container.bob) * 1.5
  const dest = container.dest

  ctx.fillStyle = `rgba(${hexRgb(dest.color)}, 0.18)`
  ctx.beginPath()
  ctx.arc(cellX + CELL / 2, cellY + CELL / 2 + bob, CELL * 0.65, 0, Math.PI * 2)
  ctx.fill()

  const cw = CELL - 8,
    ch = CELL - 12
  const cx = cellX + 4,
    cy = cellY + 6 + bob
  ctx.fillStyle = dest.color
  ctx.fillRect(cx, cy, cw, ch)
  ctx.fillStyle = 'rgba(255,255,255,0.18)'
  ctx.fillRect(cx, cy, cw, 2)
  ctx.fillStyle = 'rgba(0,0,0,0.35)'
  ctx.fillRect(cx, cy + ch - 2, cw, 2)
  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 0.6
  for (let i = 1; i < 4; i++) {
    const lx = cx + (cw / 4) * i
    ctx.beginPath()
    ctx.moveTo(lx, cy + 2)
    ctx.lineTo(lx, cy + ch - 2)
    ctx.stroke()
  }
  ctx.strokeStyle = 'rgba(0,0,0,0.5)'
  ctx.lineWidth = 1
  ctx.strokeRect(cx, cy, cw, ch)
  ctx.fillStyle = 'rgba(255,255,255,0.95)'
  ctx.font = 'bold 9px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(dest.code, cx + cw / 2, cy + ch / 2)
  ctx.textBaseline = 'alphabetic'
}

function drawCrane(
  ctx: CanvasRenderingContext2D,
  cellX: number,
  cellY: number,
  crane: CraneEntity,
) {
  const cx = cellX + CELL / 2
  const cy = cellY + CELL / 2

  const reachR = CELL * 1.1 + Math.sin(crane.pulse) * 2
  ctx.strokeStyle = `rgba(${hexRgb(crane.dest.color)}, ${0.35 + Math.sin(crane.pulse) * 0.15})`
  ctx.lineWidth = 1.5
  ctx.setLineDash([4, 4])
  ctx.beginPath()
  ctx.arc(cx, cy, reachR, 0, Math.PI * 2)
  ctx.stroke()
  ctx.setLineDash([])

  ctx.fillStyle = '#3a2c12'
  ctx.fillRect(cellX + 2, cellY + CELL - 7, CELL - 4, 5)
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(cellX + 4, cellY + CELL - 6, CELL - 8, 3)

  ctx.fillStyle = '#5a4520'
  ctx.fillRect(cx - 3, cellY + 4, 6, CELL - 10)
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(cx - 2, cellY + 4, 4, CELL - 10)
  ctx.strokeStyle = 'rgba(0,0,0,0.5)'
  ctx.lineWidth = 0.7
  for (let i = 0; i < 3; i++) {
    const ty = cellY + 6 + i * 5
    ctx.beginPath()
    ctx.moveTo(cx - 2, ty)
    ctx.lineTo(cx + 2, ty + 5)
    ctx.moveTo(cx + 2, ty)
    ctx.lineTo(cx - 2, ty + 5)
    ctx.stroke()
  }

  const swing = Math.sin(crane.armSwing) * 0.15
  ctx.save()
  ctx.translate(cx, cellY + 6)
  ctx.rotate(swing)
  ctx.strokeStyle = '#5a4520'
  ctx.lineWidth = 5
  ctx.beginPath()
  ctx.moveTo(-CELL * 0.45, 0)
  ctx.lineTo(CELL * 0.45, 0)
  ctx.stroke()
  ctx.strokeStyle = '#c9a648'
  ctx.lineWidth = 3
  ctx.beginPath()
  ctx.moveTo(-CELL * 0.45, 0)
  ctx.lineTo(CELL * 0.45, 0)
  ctx.stroke()
  const hookX = CELL * 0.35
  ctx.strokeStyle = '#7a7a7a'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(hookX, 0)
  ctx.lineTo(hookX, 6)
  ctx.stroke()
  ctx.fillStyle = '#7a7a7a'
  ctx.beginPath()
  ctx.arc(hookX, 8, 2, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  const panelW = 36,
    panelH = 14
  const panelX = cx - panelW / 2
  const panelY = cellY - panelH - 4
  ctx.fillStyle = '#0d1218'
  ctx.fillRect(panelX, panelY, panelW, panelH)
  ctx.strokeStyle = crane.dest.color
  ctx.lineWidth = 1.5
  ctx.strokeRect(panelX, panelY, panelW, panelH)
  ctx.fillStyle = crane.dest.color
  ctx.font = 'bold 10px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(crane.dest.code, cx, panelY + panelH / 2)
  ctx.textBaseline = 'alphabetic'
}

function drawTruck(
  ctx: CanvasRenderingContext2D,
  cab: GridCell,
  trailers: Trailer[],
  dir: Direction,
  playX: number,
  playY: number,
  time: number,
) {
  for (let i = trailers.length - 1; i >= 0; i--) {
    const t = trailers[i]
    const cx = playX + t.col * CELL
    const cy = playY + t.row * CELL
    drawTrailerCell(ctx, cx, cy, t)
  }

  const ccx = playX + cab.col * CELL
  const ccy = playY + cab.row * CELL
  drawCab(ctx, ccx, ccy, dir, time)
}

function drawCab(
  ctx: CanvasRenderingContext2D,
  cellX: number,
  cellY: number,
  dir: Direction,
  time: number,
) {
  ctx.fillStyle = '#c9a648'
  ctx.fillRect(cellX + 3, cellY + 3, CELL - 6, CELL - 6)
  ctx.fillStyle = 'rgba(255,255,255,0.22)'
  ctx.fillRect(cellX + 3, cellY + 3, CELL - 6, 2)
  ctx.fillStyle = 'rgba(0,0,0,0.3)'
  ctx.fillRect(cellX + 3, cellY + CELL - 5, CELL - 6, 2)
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(cellX + 3, cellY + CELL - 8, CELL - 6, 2)
  ctx.strokeStyle = '#5a4520'
  ctx.lineWidth = 1.2
  ctx.strokeRect(cellX + 3, cellY + 3, CELL - 6, CELL - 6)

  const fcx = cellX + CELL / 2 + dir.dx * 6
  const fcy = cellY + CELL / 2 + dir.dy * 6
  ctx.fillStyle = '#1a2236'
  ctx.fillRect(fcx - 4, fcy - 4, 8, 8)
  const glassGrad = ctx.createLinearGradient(fcx - 4, fcy - 4, fcx + 4, fcy + 4)
  glassGrad.addColorStop(0, '#3a5a8a')
  glassGrad.addColorStop(1, '#1a2540')
  ctx.fillStyle = glassGrad
  ctx.fillRect(fcx - 3, fcy - 3, 6, 6)
  ctx.fillStyle = 'rgba(255,255,255,0.3)'
  ctx.fillRect(fcx - 3, fcy - 3, 2, 1)

  ctx.fillStyle = '#0d0d0d'
  if (dir.dx !== 0) {
    ctx.fillRect(cellX + 5, cellY + 1, 4, 3)
    ctx.fillRect(cellX + CELL - 9, cellY + 1, 4, 3)
    ctx.fillRect(cellX + 5, cellY + CELL - 4, 4, 3)
    ctx.fillRect(cellX + CELL - 9, cellY + CELL - 4, 4, 3)
  } else {
    ctx.fillRect(cellX + 1, cellY + 5, 3, 4)
    ctx.fillRect(cellX + 1, cellY + CELL - 9, 3, 4)
    ctx.fillRect(cellX + CELL - 4, cellY + 5, 3, 4)
    ctx.fillRect(cellX + CELL - 4, cellY + CELL - 9, 3, 4)
  }

  const beaconAlpha = 0.5 + Math.sin(time * 0.4) * 0.5
  ctx.fillStyle = `rgba(220, 60, 60, ${beaconAlpha})`
  ctx.fillRect(cellX + CELL / 2 - 1, cellY + 5, 2, 2)
}

function drawTrailerCell(
  ctx: CanvasRenderingContext2D,
  cellX: number,
  cellY: number,
  container: Trailer | null,
) {
  ctx.fillStyle = '#2a2a2a'
  ctx.fillRect(cellX + 2, cellY + 4, CELL - 4, CELL - 8)
  ctx.fillStyle = 'rgba(0,0,0,0.3)'
  ctx.fillRect(cellX + 2, cellY + CELL / 2, CELL - 4, 1)
  ctx.fillStyle = '#5a5a5a'
  ctx.fillRect(cellX + 2, cellY + 4, CELL - 4, 1)
  ctx.fillRect(cellX + 2, cellY + CELL - 5, CELL - 4, 1)
  ctx.fillStyle = '#5a5a5a'
  ctx.fillRect(cellX + 3, cellY + 4, 2, 2)
  ctx.fillRect(cellX + CELL - 5, cellY + 4, 2, 2)
  ctx.fillRect(cellX + 3, cellY + CELL - 6, 2, 2)
  ctx.fillRect(cellX + CELL - 5, cellY + CELL - 6, 2, 2)

  if (container) {
    const cw = CELL - 8,
      ch = CELL - 14
    const cx = cellX + 4,
      cy = cellY + 7
    ctx.fillStyle = container.dest.color
    ctx.fillRect(cx, cy, cw, ch)
    ctx.fillStyle = 'rgba(255,255,255,0.18)'
    ctx.fillRect(cx, cy, cw, 1)
    ctx.fillStyle = 'rgba(0,0,0,0.3)'
    ctx.fillRect(cx, cy + ch - 1, cw, 1)
    ctx.strokeStyle = 'rgba(0,0,0,0.5)'
    ctx.lineWidth = 0.8
    ctx.strokeRect(cx, cy, cw, ch)
    ctx.fillStyle = 'rgba(255,255,255,0.95)'
    ctx.font = 'bold 8px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(container.dest.code, cx + cw / 2, cy + ch / 2)
    ctx.textBaseline = 'alphabetic'
  }
}

const styles: Record<string, CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    background: '#080e18',
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    color: '#cfd8e8',
    zIndex: 9999,
    overflow: 'hidden',
  },
  canvas: { width: '100%', height: '100%', display: 'block', touchAction: 'none' },
  hud: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '14px 22px',
    display: 'flex',
    gap: 28,
    alignItems: 'center',
    fontSize: 11,
    letterSpacing: '0.18em',
    pointerEvents: 'none',
    zIndex: 2,
    background: 'linear-gradient(to bottom, rgba(8,14,24,0.85), rgba(8,14,24,0))',
  },
  hudGroup: { display: 'flex', flexDirection: 'column', gap: 2 },
  hudLabel: { color: '#5a6478', fontSize: 9 },
  hudValue: { color: '#ffd166', fontSize: 13, letterSpacing: '0.1em' },
  restart: {
    marginLeft: 'auto',
    pointerEvents: 'auto',
    background: 'transparent',
    border: '1px solid #4a3a18',
    color: '#ffd166',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  close: {
    marginLeft: 8,
    pointerEvents: 'auto',
    background: 'transparent',
    border: '1px solid #2a3548',
    color: '#7a8398',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    background: 'rgba(8, 14, 24, 0.65)',
    zIndex: 3,
    pointerEvents: 'none',
  },
  overlayTitle: {
    fontSize: 38,
    letterSpacing: '0.32em',
    color: '#ffd166',
  },
  overlaySub: { fontSize: 12, letterSpacing: '0.18em', color: '#7a8398' },
  overlaySubLarge: { fontSize: 13, letterSpacing: '0.22em', color: '#cfd8e8' },
  controls: {
    marginTop: 8,
    fontSize: 10,
    letterSpacing: '0.18em',
    color: '#7a8398',
    textAlign: 'center',
    lineHeight: 1.6,
  },
  report: {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid #2a3548',
    padding: '16px 22px',
    fontSize: 11,
    lineHeight: 1.7,
    color: '#cfd8e8',
    margin: 0,
    maxWidth: 460,
  },
}
